import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import MediaViewer from './MediaViewer';
import { FileText, Image as ImageIcon, Video, Search, ChevronRight, AlertCircle } from 'lucide-react';
import './Tutorials.css';
import Preloader from '../../../../components/Preloader/Preloader';

const Tutorials = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const res = await apiFetch('/api/tutorials');
      const d = await res.json();
      if (Array.isArray(d.tutorials)) {
        setTutorials(d.tutorials);
      } else {
        setTutorials([]);
      }
    } catch (err) {
      console.error('Error fetching tutorials:', err);
      setError('Failed to load tutorials. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredTutorials = tutorials.filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) || 
                          t.description.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'All' || t.fileType === filter;
    return matchesSearch && matchesFilter;
  });

  const getFileIcon = (type) => {
    switch (type) {
      case 'PDF': return <FileText className="tut-card__icon-svg text-blue-500" />;
      case 'Image': return <ImageIcon className="tut-card__icon-svg text-green-500" />;
      case 'Video': return <Video className="tut-card__icon-svg text-purple-500" />;
      default: return <FileText className="tut-card__icon-svg" />;
    }
  };

  if (loading) return <Preloader message="Loading tutorials..." />;

  return (
    <div className="tutorials-page">
      <header className="tutorials-header">
        <div className="tutorials-header__content">
          <h1 className="tutorials-header__title">Academy & Documentation</h1>
          <p className="tutorials-header__subtitle">Learn how to maximize your earnings and master the Alpha Wealth platform.</p>
        </div>
      </header>

      <div className="tutorials-controls">
        <div className="tutorials-search">
          <Search size={18} className="tutorials-search__icon" />
          <input 
            type="text" 
            placeholder="Search tutorials..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="tutorials-search__input"
          />
        </div>

        <div className="tutorials-filters">
          {['All', 'PDF', 'Image', 'Video'].map(f => (
            <button 
              key={f}
              onClick={() => setFilter(f)}
              className={`tutorials-filter-btn ${filter === f ? 'tutorials-filter-btn--active' : ''}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="tutorials-error">
          <AlertCircle size={24} className="mb-2" />
          <p>{error}</p>
          <button onClick={fetchTutorials} className="tutorials-error__retry">Retry Connection</button>
        </div>
      ) : filteredTutorials.length === 0 ? (
        <div className="tutorials-empty">
           <div className="tutorials-empty__icon">📚</div>
           <h3>Resources Coming Soon</h3>
           <p>No {filter !== 'All' ? filter + ' ' : ''}tutorials are available at the moment. Please check back later for new guides and documentation.</p>
        </div>
      ) : (
        <div className="tutorials-grid">
          {filteredTutorials.map(t => (
            <div key={t.id} className="tut-card" onClick={() => setSelectedMedia(t)}>
              <div className="tut-card__preview">
                {t.fileType === 'Video' && t.thumbnailUrl ? (
                  <img src={t.thumbnailUrl} alt={t.title} className="tut-card__thumb" />
                ) : t.fileType === 'Image' ? (
                  <img src={t.fileUrl} alt={t.title} className="tut-card__thumb" />
                ) : (
                  <div className="tut-card__placeholder">
                    {getFileIcon(t.fileType)}
                  </div>
                )}
                <div className="tut-card__badge">{t.fileType}</div>
              </div>
              
              <div className="tut-card__content">
                <h3 className="tut-card__title">{t.title}</h3>
                <p className="tut-card__desc">{t.description || 'Alpha Wealth Official Guide'}</p>
                <div className="tut-card__footer">
                  <span className="tut-card__date">{t.createdAt}</span>
                  <div className="tut-card__action">
                    <span>View</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMedia && (
        <MediaViewer 
          file={selectedMedia.fileUrl} 
          type={selectedMedia.fileType} 
          title={selectedMedia.title}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
};

export default Tutorials;
